import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@/lib/utils";

/**
 * BrandBadge Component
 *
 * Status / label chips used across markets, betting, portfolio and the
 * admin console (market status, bet side, alert severity, RBAC role…).
 *
 * Contrast contract (2026-06-12): tinted chips on this light theme must
 * pair `bg-{c}/20` with the `-dark` text tokens. The component was ported
 * from a dark-theme project whose `text-{c}`/`text-{c}-light` classes are
 * pale-on-pale here (~1:1 contrast, invisible) — pinned by
 * BrandBadge.test.tsx. The unused RWA variants from that port (auction /
 * governance / rrt / ibt / pendle / pt / yt …) and the specialized badge
 * components built on them were removed the same day; restore from git
 * history if ever needed.
 */

const brandBadgeVariants = cva(
  "inline-flex items-center rounded-md border px-2.5 py-0.5 text-xs font-semibold uppercase tracking-wide transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2",
  {
    variants: {
      variant: {
        // Market lifecycle (MarketDetailHeader status map)
        active: "bg-success/20 text-success-dark border border-success/40",
        ended: "bg-gray-500/20 text-text-secondary border border-gray-500/40",

        // Semantic status
        success: "bg-success/20 text-success-dark border border-success/40",
        warning: "bg-warning/20 text-warning-dark border border-warning/40",
        danger: "bg-danger/20 text-danger-dark border border-danger/40",
        info: "bg-info/20 text-info-dark border border-info/40",

        // Brand accents
        default: "bg-amber-500/10 text-brand-accent border border-amber-500/30",
        gold: "bg-amber-500/20 text-brand-accent border border-amber-500/40",
        sapphire: "bg-blue-500/20 text-brand-primary border border-blue-500/40",
        outline: "bg-transparent text-text-primary border border-border-card",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  },
);

export interface BrandBadgeProps
  extends
    React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof brandBadgeVariants> {}

function BrandBadge({ className, variant, ...props }: BrandBadgeProps) {
  return (
    <div
      className={cn(brandBadgeVariants({ variant }), className)}
      {...props}
    />
  );
}

export { BrandBadge, brandBadgeVariants };
